import { useEffect, useRef, useState } from 'react';
import { Link, useBlocker, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, FileJson, Loader2, Save, UploadCloud } from 'lucide-react';
import { Button, Modal } from '@tes/ui';
import { api } from '../lib/api';
import { messageOf } from '../lib/errors';
import type { TemplateStatus } from '../lib/types';
import { useEditor } from '../editor/editorStore';
import { EditorCanvas } from '../editor/EditorCanvas';
import { ToolPalette } from '../editor/ToolPalette';
import { PropertiesPanel } from '../editor/PropertiesPanel';
import { SettingsPanel } from '../editor/SettingsPanel';
import { ContractPanel } from '../editor/ContractPanel';
import { ReplaceBaseFileDialog } from '../editor/ReplaceBaseFileDialog';
import { VariableLibraryModal } from '../components/variables';

const STATUS_STYLE: Record<TemplateStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  published: 'bg-emerald-100 text-emerald-700',
  archived: 'bg-amber-100 text-amber-700',
};

/**
 * Persist the editor. The DESIGN goes first: it is the call that fails
 * validation (a variable not in the library, an empty static field), and if
 * it does nothing else should have changed. Settings follow; a failure there
 * is reported as exactly that, so nobody hunts for a design problem that
 * isn't one.
 */
async function saveEditor(id: string): Promise<void> {
  const s = useEditor.getState();
  await api.saveDesign(id, s.toDesignPayload());
  try {
    await api.updateTemplate(id, {
      name: s.name,
      description: s.description,
      requiresSignature: s.requiresSignature,
      signingOrder: s.signingOrder,
    });
  } catch (e) {
    throw new Error(
      `Design saved, but the template settings (name, description, signing) were not: ${messageOf(e, 'request failed')}`,
    );
  }
  s.markClean();
}

export default function EditorPage() {
  const { id = '' } = useParams();
  const { data, refetch } = useQuery({ queryKey: ['template', id], queryFn: () => api.getTemplate(id) });
  const initedFor = useRef<string | null>(null);

  const name = useEditor((s) => s.name);
  const dirty = useEditor((s) => s.dirty);
  const selectedUid = useEditor((s) => s.selectedUid);
  const libraryOpen = useEditor((s) => s.libraryOpen);
  const baseFileOpen = useEditor((s) => s.baseFileOpen);

  const [tab, setTab] = useState<'field' | 'settings'>('settings');
  const [showContract, setShowContract] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data && initedFor.current !== data.id) {
      useEditor.getState().init(data);
      initedFor.current = data.id;
    }
  }, [data]);

  useEffect(() => {
    if (selectedUid) setTab('field');
  }, [selectedUid]);

  // Unsaved-changes guard, both ways out: in-app navigation is held by the
  // router until the person decides; a tab close / reload gets the browser's
  // own "leave site?" prompt.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers need a returnValue to show the prompt.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const saveAll = async () => {
    await saveEditor(id);
    await refetch();
  };

  const saveMutation = useMutation({
    mutationFn: saveAll,
    onError: (e) => setError(messageOf(e, 'Save failed')),
    onSuccess: () => setError(null),
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      await saveEditor(id);
      await api.publish(id);
      await refetch();
    },
    onError: (e) => setError(messageOf(e, 'Publish failed')),
    onSuccess: () => {
      setError(null);
      setShowContract(true);
    },
  });

  const status: TemplateStatus = data?.status ?? 'draft';
  const busy = saveMutation.isPending || publishMutation.isPending;

  return (
    <div className="flex h-screen flex-col bg-slate-50 font-sans text-slate-900">
      {/* top bar */}
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-2">
        <div className="flex items-center gap-3">
          <Link to="/" className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Back to templates">
            <ArrowLeft size={18} />
          </Link>
          <span className="font-semibold">{name || 'Template'}</span>
          <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[status]}`}>
            {status}
          </span>
          {dirty && <span className="text-xs text-amber-600">● unsaved</span>}
        </div>
        <div className="flex items-center gap-2">
          {error && (
            <span className="mr-2 max-w-md truncate text-xs text-red-600" title={error}>
              {error}
            </span>
          )}
          <Button variant="secondary" onClick={() => setShowContract(true)}>
            <FileJson size={16} className="mr-1" /> Contract
          </Button>
          <Button variant="outline" onClick={() => saveMutation.mutate()} disabled={busy || !dirty}>
            {saveMutation.isPending ? (
              <Loader2 size={16} className="mr-1 animate-spin" />
            ) : (
              <Save size={16} className="mr-1" />
            )}
            Save
          </Button>
          <Button onClick={() => publishMutation.mutate()} disabled={busy}>
            {publishMutation.isPending ? (
              <Loader2 size={16} className="mr-1 animate-spin" />
            ) : (
              <UploadCloud size={16} className="mr-1" />
            )}
            Publish
          </Button>
        </div>
      </header>

      {/* body */}
      <div className="flex flex-1 overflow-hidden">
        <ToolPalette />
        <div className="flex-1 overflow-hidden">{data && <EditorCanvas />}</div>
        <aside className="flex w-80 flex-col border-l border-slate-200 bg-white">
          <div className="flex border-b border-slate-200 text-sm">
            <button
              type="button"
              className={`flex-1 py-2 ${tab === 'field' ? 'border-b-2 border-brand-500 font-medium text-brand-700' : 'text-slate-500'}`}
              onClick={() => setTab('field')}
            >
              Field
            </button>
            <button
              type="button"
              className={`flex-1 py-2 ${tab === 'settings' ? 'border-b-2 border-brand-500 font-medium text-brand-700' : 'text-slate-500'}`}
              onClick={() => setTab('settings')}
            >
              Settings
            </button>
          </div>
          <div className="flex-1 overflow-auto">
            {tab === 'field' ? <PropertiesPanel /> : <SettingsPanel />}
          </div>
        </aside>
      </div>

      {showContract && <ContractPanel templateId={id} onClose={() => setShowContract(false)} />}
      {libraryOpen && (
        <VariableLibraryModal onClose={() => useEditor.getState().setLibraryOpen(false)} />
      )}
      {baseFileOpen && (
        <ReplaceBaseFileDialog
          templateId={id}
          saveFirst={saveAll}
          onClose={() => useEditor.getState().setBaseFileOpen(false)}
          onReplaced={(t) => {
            // Re-seed from the swap response: the id is unchanged, so the
            // effect above won't re-init, and the store still holds the OLD
            // pageSizes the canvas renders against.
            useEditor.getState().init(t);
            useEditor.getState().setBaseFileOpen(false);
            void refetch();
          }}
        />
      )}
      {blocker.state === 'blocked' && (
        <Modal
          title="Leave without saving?"
          onClose={() => blocker.reset()}
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => blocker.reset()} data-autofocus>
                Stay
              </Button>
              <Button variant="danger" onClick={() => blocker.proceed()}>
                Discard changes
              </Button>
            </>
          }
        >
          <p className="text-sm text-slate-600">
            This template has unsaved changes. Leaving now throws them away.
          </p>
        </Modal>
      )}
    </div>
  );
}
